import { ICatalogue } from "../../apis/useGetCatalogueData/types";
import { globalEvents } from "../../common/helpers/GlobalEvents";
import { createContextReducer } from "../../common/helpers/createContextReducer";
import {
	getLocalStorageValue,
	setLocalStorageValue,
} from "../../common/helpers/localStorageHelper";
import { TOAST_EVENTS } from "../../components/Toast/constants";
import { FINGERPRINT_KEY } from "../../services/fingerprint";
import { checkCartHasItem } from "./helpers";
import { ICartContext } from "./types";

const CART_STORAGE_KEY = "cart";
const CART_STATE_STORAGE_KEY = "cart_state";

interface ICartDiningState {
	is_takeaway: boolean;
	takeaway_byob: boolean;
	order_level_takeaway_fee: number;
	initial_takeaway_selection: boolean;
	selectedGrillNumber?: string | null;
}

export const initialState: ICartContext = {
	cart: [],
	currentSession: "",
	is_takeaway: false,
	takeaway_byob: false,
	initial_takeaway_selection: true,
	order_level_takeaway_fee: 0,
	selectedGrillNumber: undefined,
};

const saveDiningState = (sessionId: string, diningState: ICartDiningState): void => {
	setLocalStorageValue(`${sessionId}-${CART_STATE_STORAGE_KEY}`, diningState);
};

const loadDiningState = (sessionId: string): ICartDiningState | null => {
	return getLocalStorageValue(`${sessionId}-${CART_STATE_STORAGE_KEY}`, null);
};

const persistDiningState = (state: ICartContext): void => {
	if (!state.currentSession) return;
	saveDiningState(state.currentSession, {
		is_takeaway: state.is_takeaway,
		takeaway_byob: state.takeaway_byob,
		order_level_takeaway_fee: state.order_level_takeaway_fee,
		initial_takeaway_selection: state.initial_takeaway_selection,
		selectedGrillNumber: state.selectedGrillNumber,
	});
};

export const cartReducer = {
	CART_INIT: (state: ICartContext) => {
		const session_id = getLocalStorageValue(FINGERPRINT_KEY, null);

		if (state.currentSession === session_id) return;
		state.currentSession = session_id;

		// Load cart data
		try {
			const cart = getLocalStorageValue(`${session_id}-${CART_STORAGE_KEY}`, []);

			state.cart = cart;

			// Load dining state if session_id exists
			if (session_id) {
				const diningState = loadDiningState(session_id);

				if (diningState) {
					state.is_takeaway = diningState.is_takeaway;
					state.takeaway_byob = diningState.takeaway_byob;
					state.order_level_takeaway_fee = diningState.order_level_takeaway_fee;
					state.initial_takeaway_selection = diningState.initial_takeaway_selection;
					state.selectedGrillNumber = diningState.selectedGrillNumber;
				}
			}
		} catch (error) {
			console.error("cart init failed", error);
		}
	},
	ADD_ITEM: (state: ICartContext, menuItem: ICatalogue) => {
		if (!menuItem) return;
		const check = checkCartHasItem(menuItem, state.cart);

		if (check === -1) {
			const newCart = [...state.cart, menuItem];

			state.cart = newCart;

			setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, newCart);
		} else {
			state.cart[check].quantity = (state.cart[check].quantity || 0) + (menuItem.quantity || 0);
			setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, state.cart);
		}
		setTimeout(() => {
			globalEvents.emit(TOAST_EVENTS.SHOW_ADD_ITEM_TOAST);
		}, 100);
	},
	REMOVE_CART: (state: ICartContext, indexToRemove: number) => {
		if (indexToRemove >= 0 && indexToRemove < state.cart.length) {
			state.cart.splice(indexToRemove, 1);
			setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, state.cart);
		}
	},
	UPDATE_CART: (state: ICartContext, updatedCart: ICatalogue[]) => {
		state.cart = updatedCart;
		setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, updatedCart);
	},
	UPDATE_CART_ITEM: (state: ICartContext, payload: { index: number; item: ICatalogue }) => {
		const { index, item } = payload;

		if (index >= 0 && index < state.cart.length) {
			state.cart[index] = item;
			setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, state.cart);
		}
	},
	CLEAN_CART: (state: ICartContext) => {
		state.cart = [];
		state.selectedGrillNumber = undefined;
		setLocalStorageValue(`${state.currentSession}-${CART_STORAGE_KEY}`, []);
		persistDiningState(state);
	},
	SET_GRILL_NUMBER: (state: ICartContext, grillId: string | null) => {
		state.selectedGrillNumber = grillId;
		persistDiningState(state);
	},
	TAKEAWAY: (state: ICartContext, fee: number) => {
		state.is_takeaway = true;
		state.takeaway_byob = false;
		state.order_level_takeaway_fee = fee;
		persistDiningState(state);
	},
	DINEIN: (state: ICartContext) => {
		state.is_takeaway = false;
		state.takeaway_byob = false;
		persistDiningState(state);
	},
	TAKEAWAY_BYOB: (state: ICartContext, takeaway: { byob: boolean; fee: number }) => {
		state.is_takeaway = true;
		state.takeaway_byob = takeaway.byob;
		state.order_level_takeaway_fee = takeaway.fee;
		persistDiningState(state);
	},
	INITIAL_TAKEAWAY_SELECTED: (state: ICartContext) => {
		state.initial_takeaway_selection = false;
		persistDiningState(state);
	},
};

const {
	AppProvider: CartProvider,
	useContextActions: useCartActions,
	useContextPartial: useCartContext,
} = createContextReducer(initialState, cartReducer);

export { CartProvider, useCartActions, useCartContext };
